/**
 * The consent seams the kernel keeps when the privacy module is not compiled in.
 *
 * The kernel owns consent *state* (`ConsentEngine`) because `track()` reads a
 * status on every event. It does not own consent *resolution* — CMP detection,
 * TCF/USP reading and the opt-out surface live in the privacy module, which
 * registers itself here at install.
 *
 * With no module registered the gate settles immediately and the privacy api is
 * inert. Consent then stays `unknown` and `co` is omitted: a bundle built
 * without the module has no signal, and reporting one would be a lie.
 */

export type ConsentInit = 'granted' | 'denied';
export type PrivacyInit = 'opted-in' | 'opted-out';
export type PrivacyStatus = 'opted-in' | 'opted-out';

/** `KueezSDK.privacy` — the host-facing "Do Not Sell" surface. */
export interface PrivacyApi {
  optOut(): void;
  optIn(): void;
  status(): PrivacyStatus;
}

export const INERT_PRIVACY_API: PrivacyApi = {
  optOut() {},
  optIn() {},
  status: () => 'opted-in',
};

export interface ConsentGate {
  /** Resolves once consent has settled as far as it is going to for this page. */
  settled(): Promise<void>;
  destroy(): void;
}

const INERT_GATE: ConsentGate = {
  settled: () => Promise.resolve(),
  destroy() {},
};

let gate: ConsentGate = INERT_GATE;

export function setConsentGate(next: ConsentGate): void {
  gate = next;
}

/** Awaited by init before identity resolves and lifecycle events fire. */
export function awaitConsentSettled(): Promise<void> {
  return gate.settled();
}

/** True when a privacy module registered a gate — i.e. some regime applies. */
export function hasConsentGate(): boolean {
  return gate !== INERT_GATE;
}

export function destroyConsentGate(): void {
  gate.destroy();
  gate = INERT_GATE;
}
